import { Button } from '../../components/Button/Button'
import { NavLink } from 'react-router-dom'
import { ArrowRight, Layers, Shield } from 'lucide-react'
import { useQuoteModal } from '../../context/QuoteContext'

import heroFacilityImg from '../../assets/images/africa-pipetek-facility.webp'
import fleetImg from '../../assets/images/africa-pipetek-fleet-delivery-vehicles.webp'

function Home() {
	const { openQuoteModal } = useQuoteModal()

	const productCategories = [
		{
			icon: Layers,
			title: 'PVC Piping & Fittings',
			desc: 'Quality PVC solutions engineered for construction, agricultural, and industrial reticulation systems.',
			link: '/products#pvc',
		},
		{
			icon: Shield,
			title: 'HDPE Pressure Piping',
			desc: 'Durable high-density polyethylene pipes built for mining, municipal water, and fluid conveyance.',
			link: '/products#hdpe',
		},
	]

	return (
		<main className='bg-brand-light min-h-screen'>
			{/* 1. HERO SECTION */}
			<section className='py-12 lg:py-16 bg-white border-b border-brand-border'>
				<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
					<div className='grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center'>
						{/* Left Content Column */}
						<div className='lg:col-span-7 flex flex-col items-start'>
							<h1 className='font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-dark leading-tight'>
								Welcome to{' '}
								<span className='text-brand-blue'>Africa Pipetek</span>
							</h1>

							<div className='mt-4 space-y-3 text-slate-600 text-base leading-relaxed max-w-2xl'>
								<p>
									Africa Pipetek was established in South Africa in 2025,
									drawing on extensive experience gained across countries around
									the world. We specialize in premium-quality HDPE, PVC, and
									fittings.
								</p>
								<p>
									We offer a diverse range of products designed to meet the
									evolving needs of the construction, agriculture, mining, and
									industrial sectors.
								</p>
								<p>
									Through advanced technology and state-of-the-art manufacturing
									processes, we are committed to delivering uncompromising
									quality and innovation.
								</p>
								<p className='font-medium text-brand-dark'>
									Our core value is to understand our customers' needs and
									provide precise solutions that consistently exceed
									expectations.
								</p>
							</div>

							{/* Action Buttons */}
							<div className='mt-6 flex flex-col sm:flex-row gap-4 w-full sm:w-auto'>
								<Button variant='primary' size='md' onClick={openQuoteModal}>
									Request a Quote
								</Button>

								<NavLink to='/about'>
									<Button
										variant='outline'
										size='md'
										className='w-full sm:w-auto'>
										More About Us
									</Button>
								</NavLink>
							</div>
						</div>

						{/* Right Column - Facility Photo */}
						<div className='lg:col-span-5 flex justify-center lg:justify-end'>
							<div className='w-full max-w-md lg:max-w-none relative rounded-2xl overflow-hidden shadow-md border border-brand-border bg-slate-100'>
								<img
									src={heroFacilityImg}
									alt='Africa Pipetek 20,000 sqm manufacturing facility in Alrode'
									className='w-full h-[320px] sm:h-[400px] object-cover'
								/>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* 2. ABOUT SUMMARY SECTION */}
			<section className='py-12 md:py-16 bg-white border-b border-brand-border'>
				<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
					<div className='grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center'>
						{/* Image Column - Delivery Fleet Photo */}
						<div className='lg:col-span-5 order-2 lg:order-1 relative rounded-2xl overflow-hidden shadow-md border border-brand-border bg-slate-100'>
							<img
								src={fleetImg}
								alt='Africa Pipetek commercial delivery vehicle fleet'
								className='w-full h-[300px] sm:h-[360px] object-cover'
							/>
						</div>

						{/* Text Column */}
						<div className='lg:col-span-7 order-1 lg:order-2 flex flex-col items-start'>
							<h2 className='font-heading text-2xl sm:text-3xl font-bold text-brand-dark'>
								Reliable Piping Infrastructure
							</h2>

							<p className='mt-3 text-slate-600 text-base leading-relaxed'>
								Built on a 20,000 m² site in Alrode, Johannesburg, our facility
								features eight advanced production lines to support world-class
								manufacturing, consistent quality, and efficient fleet delivery.
							</p>

							<div className='mt-6'>
								<NavLink
									to='/about'
									className='inline-flex items-center gap-2 font-heading text-sm font-bold text-brand-blue hover:text-brand-dark transition-colors group'>
									Read Full Company Details
									<ArrowRight className='w-4 h-4 transition-transform group-hover:translate-x-1' />
								</NavLink>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* 3. PRODUCT RANGE SUMMARY (FULL-WIDTH GRID) */}
			<section className='py-12 md:py-16 bg-brand-light'>
				<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
					<div className='flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4'>
						<div>
							<h2 className='font-heading text-2xl font-bold text-brand-dark'>
								Our Products
							</h2>
							<p className='text-sm text-slate-600 mt-1'>
								Explore our core PVC and HDPE range.
							</p>
						</div>

						<NavLink
							to='/products'
							className='font-heading text-sm font-semibold text-brand-blue hover:text-brand-dark flex items-center gap-1.5 group transition-colors'>
							View All Products{' '}
							<ArrowRight className='w-4 h-4 transition-transform group-hover:translate-x-1' />
						</NavLink>
					</div>

					<div className='grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 w-full'>
						{productCategories.map((cat, idx) => {
							const Icon = cat.icon
							return (
								<div
									key={idx}
									className='bg-white p-6 sm:p-8 rounded-xl border border-brand-border shadow-sm hover:border-brand-blue/40 transition-all flex flex-col justify-between group'>
									<div>
										<div className='w-10 h-10 rounded-lg bg-slate-100 text-brand-dark flex items-center justify-center mb-4 group-hover:bg-brand-blue group-hover:text-white transition-colors'>
											<Icon className='w-5 h-5' />
										</div>

										<h3 className='font-heading text-lg font-bold text-brand-dark mb-2'>
											{cat.title}
										</h3>

										<p className='text-slate-600 text-sm leading-relaxed'>
											{cat.desc}
										</p>
									</div>

									<NavLink
										to={cat.link}
										className='mt-6 pt-3 border-t border-slate-100 font-heading text-sm font-semibold text-brand-dark group-hover:text-brand-blue flex items-center justify-between transition-colors'>
										View Range <span>→</span>
									</NavLink>
								</div>
							)
						})}
					</div>
				</div>
			</section>
		</main>
	)
}

export default Home
